/**
 * ============================================================================
 * COMPONENTE: NotificationBell
 * ============================================================================
 * Versão: 1.0
 * Data: 25/11/2025
 * Descrição: Sino de notificações na navbar com badge e dropdown
 * ============================================================================
 */

import React, { useEffect, useState, useRef } from 'react';
import { notificationsService } from '../services/notificationsService';

interface Notificacao {
  id: number;
  tipo: string;
  titulo: string;
  mensagem: string;
  prioridade: string;
  status: string;
  criado_em: string;
  referente_tipo?: string;
  referente_id?: string;
}

export default function NotificationBell() {
  const [naoLidas, setNaoLidas] = useState(0);
  const [notificacoes, setNotificacoes] = useState<Notificacao[]>([]);
  const [mostrarDropdown, setMostrarDropdown] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const dropdownRef = useRef<HTMLLIElement>(null);

  // Buscar contagem de não lidas a cada 30 segundos
  useEffect(() => {
    buscarContagem();
    const intervalo = setInterval(buscarContagem, 30000);

    return () => clearInterval(intervalo);
  }, []);

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setMostrarDropdown(false);
      }
    }

    if (mostrarDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [mostrarDropdown]);

  const buscarContagem = async () => {
    try {
      const total = await notificationsService.contarNaoLidas();
      setNaoLidas(total);
    } catch (error) {
      console.error('[NOTIFICATION-BELL] Erro ao buscar contagem:', error);
    }
  };

  const buscarNotificacoes = async () => {
    try {
      setCarregando(true);
      const resultado = await notificationsService.listar({
        status: 'nao_lidas',
        page: 1,
        pageSize: 10
      });

      setNotificacoes(resultado.items);
    } catch (error) {
      console.error('[NOTIFICATION-BELL] Erro ao buscar notificações:', error);
    } finally {
      setCarregando(false);
    }
  };

  const toggleDropdown = async () => {
    if (!mostrarDropdown) {
      await buscarNotificacoes();
    }
    setMostrarDropdown(!mostrarDropdown);
  };

  const marcarComoLida = async (id: number) => {
    try {
      await notificationsService.marcarComoLidas([id]);
      // Atualizar lista local
      setNotificacoes(prev => prev.filter(n => n.id !== id));
      setNaoLidas(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('[NOTIFICATION-BELL] Erro ao marcar como lida:', error);
    }
  };

  const marcarTodasComoLidas = async () => {
    try {
      const ids = notificacoes.map(n => n.id);
      if (ids.length === 0) return;

      await notificationsService.marcarComoLidas(ids);
      setNotificacoes([]);
      setNaoLidas(0);
    } catch (error) {
      console.error('[NOTIFICATION-BELL] Erro ao marcar todas como lidas:', error);
    }
  };

  const getIconePorTipo = (tipo: string): string => {
    const icones: Record<string, string> = {
      'consulta_confirmada': 'fa-calendar-check',
      'consulta_cancelada': 'fa-calendar-times',
      'consulta_alterada': 'fa-calendar-edit',
      'lembrete_consulta': 'fa-clock',
      'pagamento_aprovado': 'fa-check-circle',
      'pagamento_pendente': 'fa-exclamation-circle',
      'prontuario_pendente': 'fa-file-medical',
      'plano_vencendo': 'fa-exclamation-triangle',
      'sistema': 'fa-info-circle',
      'admin_broadcast': 'fa-bullhorn'
    };

    return icones[tipo] || 'fa-bell';
  };

  const getCorPorPrioridade = (prioridade: string): string => {
    const cores: Record<string, string> = {
      'urgente': 'text-danger',
      'alta': 'text-warning',
      'normal': 'text-info',
      'baixa': 'text-secondary'
    };

    return cores[prioridade] || 'text-info';
  };

  const formatarTempo = (dataString: string): string => {
    const data = new Date(dataString);
    const agora = new Date();
    const diffMs = agora.getTime() - data.getTime();
    const diffMinutos = Math.floor(diffMs / 60000);

    if (diffMinutos < 1) return 'Agora';
    if (diffMinutos < 60) return `${diffMinutos}m atrás`;

    const diffHoras = Math.floor(diffMinutos / 60);
    if (diffHoras < 24) return `${diffHoras}h atrás`;

    const diffDias = Math.floor(diffHoras / 24);
    if (diffDias < 7) return `${diffDias}d atrás`;

    return data.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  };

  return (
    <li className="nav-item dropdown" ref={dropdownRef}>
      <a
        className="nav-link position-relative"
        href="#"
        onClick={(e) => {
          e.preventDefault();
          toggleDropdown();
        }}
      >
        <i className="far fa-bell"></i>
        {naoLidas > 0 && (
          <span className="badge badge-danger navbar-badge position-absolute" style={{ top: '5px', right: '5px' }}>
            {naoLidas > 99 ? '99+' : naoLidas}
          </span>
        )}
      </a>

      {mostrarDropdown && (
        <div 
          className="dropdown-menu dropdown-menu-lg dropdown-menu-right show"
          style={{ 
            display: 'block', 
            width: 'min(350px, 90vw)', 
            maxWidth: '90vw',
            maxHeight: '70vh', 
            overflowY: 'auto',
            right: '10px',
            left: 'auto',
            position: 'fixed',
            top: '50px',
            zIndex: 1050,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            borderRadius: '8px'
          }}
        >
          <div className="dropdown-header d-flex justify-content-between align-items-center">
            <span>{naoLidas} {naoLidas === 1 ? 'Notificação' : 'Notificações'}</span>
            {notificacoes.length > 0 && (
              <button
                className="btn btn-link btn-sm p-0"
                onClick={marcarTodasComoLidas}
                style={{ fontSize: '0.75rem' }}
              >
                Marcar todas como lidas
              </button>
            )}
          </div>

          <div className="dropdown-divider"></div>

          {carregando && (
            <div className="dropdown-item text-center">
              <i className="fas fa-spinner fa-spin mr-2"></i>
              Carregando...
            </div>
          )}

          {!carregando && notificacoes.length === 0 && (
            <div className="dropdown-item text-center text-muted">
              <i className="far fa-bell-slash mr-2"></i>
              Nenhuma notificação
            </div>
          )}

          {!carregando && notificacoes.map((notificacao) => (
            <React.Fragment key={notificacao.id}>
              <a
                href="#"
                className="dropdown-item"
                onClick={(e) => {
                  e.preventDefault();
                  marcarComoLida(notificacao.id);
                }}
              >
                <div className="d-flex">
                  <div className="mr-3">
                    <i className={`fas ${getIconePorTipo(notificacao.tipo)} ${getCorPorPrioridade(notificacao.prioridade)}`}></i>
                  </div>
                  <div className="flex-grow-1">
                    <p className="mb-1" style={{ fontSize: '0.875rem', fontWeight: 500 }}>
                      {notificacao.titulo}
                    </p>
                    <p className="mb-1 text-muted" style={{ fontSize: '0.75rem' }}>
                      {notificacao.mensagem.length > 100 
                        ? notificacao.mensagem.substring(0, 100) + '...' 
                        : notificacao.mensagem}
                    </p>
                    <p className="mb-0 text-muted" style={{ fontSize: '0.7rem' }}>
                      <i className="far fa-clock mr-1"></i>
                      {formatarTempo(notificacao.criado_em)}
                    </p>
                  </div>
                </div>
              </a>
              <div className="dropdown-divider"></div>
            </React.Fragment>
          ))}

          {notificacoes.length > 0 && (
            <a
              href="#"
              className="dropdown-item dropdown-footer text-center"
              onClick={(e) => {
                e.preventDefault();
                // TODO: Navegar para página completa de notificações quando implementada
                alert('Página de notificações completa será implementada em breve');
              }}
            >
              Ver todas as notificações
            </a>
          )}
        </div>
      )}
    </li>
  );
}
